/**
 * Berichtenprotocol tussen browser en server.
 *
 * Dit bestand is de ENIGE bron van waarheid voor wat de twee kanten tegen
 * elkaar zeggen. Client en server importeren allebei hieruit. Verander je een
 * veld en vergeet je de andere kant, dan valt de typecheck om in plaats van dat
 * er stil iets niet aankomt.
 */

/** Protocolversie. Ophogen zodra een bestaand bericht van vorm verandert. */
export const PROTOCOL_VERSION = 1;

/** De twee plekken in een kamer. Er is geen derde. */
export type Slot = 'a' | 'b';

export type Gender = 'man' | 'vrouw';

/**
 * Aanspreekvorm. Grammaticaal en hard: de meeste Europese talen hebben een
 * apart woord voor "jij" en "u" (te/on, du/Sie, tu/vous).
 *
 * Zonder sturing kiest een vertaalmodel bijna altijd de FORMELE variant -
 * de trainingsdata staat scheef richting nieuws en documentatie. In een
 * privegesprek klinkt dat onbedoeld afstandelijk.
 */
export type Aanspreekvorm = 'automatisch' | 'informeel' | 'formeel';

/**
 * Sfeer van het gesprek. Stuurt toon en woordkeuze, niet de grammatica.
 *
 * BEWUST LOSGEKOPPELD van de aanspreekvorm: een zakelijk gesprek waarin je
 * elkaar tutoyeert bestaat, en met een gecombineerde keuzelijst zou dat niet
 * kunnen.
 */
export type Sfeer =
  | 'neutraal'
  | 'zakelijk'
  | 'officieel'
  | 'vriendschappelijk'
  | 'partner';

export const AANSPREEKVORMEN: readonly { waarde: Aanspreekvorm; label: string; uitleg: string }[] = [
  { waarde: 'automatisch', label: 'Automatisch', uitleg: 'Het model kiest zelf' },
  { waarde: 'informeel', label: 'Je en jij', uitleg: 'Tutoyeren' },
  { waarde: 'formeel', label: 'U', uitleg: 'Vousvoyeren' },
] as const;

export const SFEREN: readonly { waarde: Sfeer; label: string; uitleg: string }[] = [
  { waarde: 'neutraal', label: 'Neutraal', uitleg: 'Geen sturing' },
  { waarde: 'zakelijk', label: 'Zakelijk', uitleg: 'Correct, zonder jargon' },
  { waarde: 'officieel', label: 'Officieel', uitleg: 'Arts, gemeente, bank' },
  { waarde: 'vriendschappelijk', label: 'Vriendschappelijk', uitleg: 'Los, spreektaal' },
  { waarde: 'partner', label: 'Partner', uitleg: 'Warm en persoonlijk' },
] as const;

/** Waar een duimpje over gaat. */
export type FeedbackKind = 'transcript' | 'translation';

export type Thumb = 'up' | 'down';

/** Status van een bericht in de vertaalketen. */
export type MessageStatus = 'pending' | 'translated' | 'failed';

// ---------------------------------------------------------------------------
// Gedeelde vormen
// ---------------------------------------------------------------------------

export interface RoomSettings {
  langA: string;
  langB: string;
  genderA: Gender;
  genderB: Gender;
  aanspreekvorm: Aanspreekvorm;
  sfeer: Sfeer;
  /**
   * De originele stemopname meesturen naar de ontvanger.
   *
   * Waarom je dit wilt: de ander hoort hoe JIJ het zegt, niet alleen de
   * synthetische stem. Dat helpt bij het leren van een taal, en het scheelt in
   * hoe persoonlijk een gesprek aanvoelt.
   *
   * Waarom het uit moet KUNNEN: een stemopname is gevoeliger dan tekst. Je
   * hoort wie iemand is en hoe die klinkt. Wie hem ontvangt, kan hem bewaren.
   * Bij een gevoelig gesprek met een onbekende zet je dit uit.
   */
  stemDelen: boolean;
  /**
   * Spellingcorrectie op de eigen tekst.
   *
   * Standaard aan. Uit om te BEOORDELEN wat het model werkelijk verstond:
   * staat er een rare zin, dan wil je weten of hij verkeerd verstaan is of
   * netjes verstaan en daarna "verbeterd". Met correctie aan zie je alleen het
   * eindresultaat en dus het verschil niet.
   */
  correctie: boolean;
  /**
   * Alleen chat: typen en lezen, geen spraak en geen microfoon.
   *
   * Voor snel heen en weer, of naast een videogesprek waar je elkaar al hoort.
   * Een stem die elke zin voorleest houdt dan op in plaats van te helpen, en
   * elke spraakaanroep is geld en dagquotum voor niets. De originele tekst
   * staat er gewoon bij, net als altijd.
   *
   * VOOR DE HELE KAMER, niet per persoon. Per persoon bestond al (het vinkje
   * "Vertaling meteen afspelen"), maar dan moet een gast dat zelf vinden en
   * aanzetten - en juist bij snelle communicatie doet niemand dat.
   */
  alleenChat: boolean;
  /**
   * Stilte na het laatste woord voordat een ingesproken zin klaar is, in ms.
   * Alleen de beheerder zet hem, via de beheerroute. Zie shared/src/pauze.ts.
   */
  pauzeMs: number;
  /** Debugbalk tonen. Alleen de beheerder krijgt dit ooit op true. */
  debug: boolean;
  /**
   * De Google Meet-link van dit gesprek, of null.
   *
   * Hier en niet alleen op het voorportaal: de beheerder kan hem TIJDENS het
   * gesprek zetten, en dan moet de knop bij de gast verschijnen zonder dat die
   * herlaadt.
   */
  meetLink: string | null;
  /**
   * Wanneer de kamer verloopt, in ms sinds 1970. Hier omdat de beheerder hem
   * tijdens het gesprek kan verlengen, en een verlooptijd die op het scherm
   * blijft staan terwijl hij verschoven is, stuurt iemand de verkeerde kant op.
   */
  expiresAt: number;
}

/**
 * Een opname van de spreker zelf.
 *
 * WORDT NIET OPGESLAGEN. Hij gaat door de server heen naar de ontvanger en
 * blijft in het geheugen zolang de kamer open is. Downloaden bewaart hem op
 * het toestel van de ontvanger - dat is diens keuze, geen opslag van ons.
 */
export interface BronAudio {
  /** webm/opus uit MediaRecorder: ongeveer 8 kB per zin, tegen 128 kB rauw. */
  mimeType: string;
  base64: string;
  durationMs: number;
}

export interface PeerState {
  present: boolean;
  /** Live transcript van de ander, terwijl die praat. Leeg als er niets loopt. */
  typing: string;
}

/** Meetwaarden per bericht. Alleen gevuld als debug aanstaat. */
export interface DebugInfo {
  translateMs: number;
  speakMs: number | null;
  model: string;
  promptTokens: number;
  outputTokens: number;
  characters: number | null;
  costUsd: number;
}

export interface ChatMessage {
  id: string;
  senderSlot: Slot;
  originalText: string;
  translatedText: string | null;
  srcLang: string;
  dstLang: string;
  status: MessageStatus;
  createdAt: number;
  /** true zodra de opname van de spreker beschikbaar is om op te vragen. */
  heeftBronAudio?: boolean;
  /**
   * true als dit bericht is ONTSTAAN uit spraak (audioUtterance/bevestig),
   * niet uit typen. Bepaalt of het transcript-duimpje getoond wordt: dat
   * gaat over "is dit wat ik zei", en dat is zinloos bij een getypt bericht.
   *
   * ONAFHANKELIJK van heeftBronAudio: die hangt af van de kamerinstelling
   * stemDelen en is dus geen betrouwbare indicator.
   */
  ingesproken?: boolean;
  debug?: DebugInfo;
}

// ---------------------------------------------------------------------------
// Van browser naar server
// ---------------------------------------------------------------------------

export type ClientMessage =
  | {
      type: 'join';
      roomId: string;
      pin: string | null;
      lang: string;
      gender: Gender;
      /** Uit een eerdere sessie, om je eigen plek terug te krijgen. */
      token?: string;
      /**
       * false: maak voor mij GEEN spraak tot ik op de afspeelknop druk.
       * Ontbreekt hij, dan geldt aan - zo werkte het altijd.
       */
      autoSpraak?: boolean;
      /**
       * true: neem de plek van mijn taal over, ook als die bezet is. Alleen
       * nadat de gebruiker WISSEL typte bij de melding plek_bezet. Het andere
       * apparaat krijgt dan 'overgenomen'.
       */
      overnemen?: boolean;
    }
  | {
      type: 'utterance';
      clientMsgId: string;
      text: string;
      // De eigen opname gaat MEE met de zin, niet als los bericht: zo horen ze
      // altijd bij elkaar en kan de audio niet aankomen zonder zijn tekst.
      bronAudio?: BronAudio;
    }
  | { type: 'typing'; text: string }
  | { type: 'feedback'; messageId: string; kind: FeedbackKind; thumb: Thumb }
  | { type: 'retry'; messageId: string }
  | { type: 'turnClaim' }
  | { type: 'turnRelease' }
  | {
      type: 'settings';
      lang?: string;
      gender?: Gender;
      confirmBeforeSend?: boolean;
      /** Zie autoSpraak bij join. Van JOU, niet van de kamer. */
      autoSpraak?: boolean;
      stemDelen?: boolean;
      /** Spelling stilzwijgend rechtzetten. Uit om de ruwe kwaliteit te zien. */
      correctie?: boolean;
      /** Zie RoomSettings. Geldt voor de hele kamer. */
      alleenChat?: boolean;
      // Toon aanpassen tijdens het gesprek: je merkt pas na drie zinnen dat
      // het te stijf staat.
      aanspreekvorm?: Aanspreekvorm;
      sfeer?: Sfeer;
    }
  /**
   * Een ingesproken zin, als AUDIO. De server schrijft hem uit en vertaalt
   * hem in EEN aanroep.
   *
   * Waarom niet de browser laten uitschrijven: op Android is gemeten dat
   * spraakherkenning en opname niet tegelijk kunnen. Wil je je eigen stem
   * meesturen, dan moet de tekst van de server komen. Kost ongeveer
   * EUR 0,00008 per zin extra - verwaarloosbaar - en het werkt ook in
   * browsers zonder spraakherkenning.
   */
  | {
      type: 'audioUtterance';
      clientMsgId: string;
      audio: BronAudio;
      /** true: eerst laten lezen. false: meteen versturen. */
      bevestigen: boolean;
    }
  /** Het concept is goedgekeurd, eventueel met een verbeterde tekst. */
  | { type: 'bevestig'; clientMsgId: string; text: string }
  | { type: 'verwerpConcept'; clientMsgId: string }
  | { type: 'requestAudio'; messageId: string; bron?: boolean }
  | { type: 'ping' };

// ---------------------------------------------------------------------------
// Van server naar browser
// ---------------------------------------------------------------------------

/** Redenen waarom de server een verzoek weigert. De client vertaalt deze naar tekst. */
export type ErrorCode =
  /**
   * Je plek is door een ANDER APPARAAT overgenomen.
   *
   * Geen fout in de gewone zin: dit is wat er hoort te gebeuren als je met
   * hetzelfde sessietoken ergens anders binnenkomt. Het staat hier omdat het
   * langs hetzelfde kanaal gaat als de andere meldingen, en omdat het oude
   * apparaat er iets mee MOET doen - stil doordraaien is precies het probleem
   * dat dit oplost. Zie neemPlek() in rooms/registry.ts.
   */
  | 'overgenomen'
  | 'room_not_found'
  | 'room_expired'
  | 'room_full'
  /**
   * De plek van de taal die je koos is bezet. Vaak door jezelf, op een ander
   * apparaat of in een ander tabblad. Je komt NIET op de andere plek: die
   * hoort bij de andere taal. Zie neemPlek() in rooms/registry.ts.
   */
  | 'plek_bezet'
  | 'pin_required'
  | 'pin_wrong'
  | 'budget_month'
  | 'budget_room'
  | 'rate_limited'
  | 'echo_suppressed'
  | 'translate_failed'
  | 'speak_failed'
  /**
   * Het spraakmodel wil niet meer vandaag.
   *
   * Apart van speak_failed omdat de OPLOSSING anders is. Een gewone fout ga je
   * opnieuw proberen; een dagquota niet - dan wacht je tot morgen of zet je
   * billing aan. Gemeten op 4 september 2026: de gratis laag geeft honderd
   * spraakaanroepen per dag, en per zin uitspreken kost er meerdere.
   */
  | 'speak_quota'
  /** Een taal die we niet kennen, of twee keer dezelfde aan beide kanten. */
  | 'bad_language'
  | 'protocol_mismatch';

export type CloseReason = 'expired' | 'closed_by_admin' | 'budget_month' | 'peer_gone';

export type ServerMessage =
  | { type: 'joined'; slot: Slot; settings: RoomSettings; peer: PeerState; history: ChatMessage[] }
  /**
   * Bewijs dat deze plek van JOU is. De client bewaart dit in sessionStorage en
   * stuurt het mee bij het opnieuw verbinden. Zonder dit krijgt een telefoon
   * die van zendmast wisselt "kamer vol" te zien - van zijn eigen plek.
   */
  | { type: 'sessieToken'; token: string }
  | { type: 'peerState'; peer: PeerState }
  /**
   * doorSlot ontbreekt als de BEHEERDER het deed via een beheerroute - bij het
   * verlengen of het zetten van de Meet-link. Die zit niet per se op een plek
   * in de kamer.
   */
  | { type: 'settingsChanged'; settings: RoomSettings; doorSlot?: Slot }
  | { type: 'message'; message: ChatMessage }
  | { type: 'messageUpdate'; message: ChatMessage }
  /**
   * Een stuk gesproken vertaling.
   *
   * Een lange zin komt in MEERDERE stukken binnen, in volgorde. Zo hoort de
   * ander het begin al terwijl de rest nog gemaakt wordt. `deel` telt vanaf 0,
   * `laatste` zegt of er nog wat komt.
   */
  | {
      type: 'audio';
      messageId: string;
      base64: string;
      sampleRate: number;
      deel: number;
      laatste: boolean;
    }
  | { type: 'bronAudio'; messageId: string; audio: BronAudio }
  /**
   * Wat de server verstond en hoe hij het zou vertalen. Gaat ALLEEN naar de
   * spreker, zodat die het kan nakijken voordat de ander het te zien krijgt.
   */
  | { type: 'concept'; clientMsgId: string; bron: string; vertaling: string }
  | { type: 'turn'; slot: Slot | null }
  | { type: 'budgetWarning'; pct: number; spentEur: number; limitEur: number }
  /**
   * Het kamerbudget is verhoogd en er mag weer vertaald worden.
   *
   * Zonder dit seintje blijft de melding "limiet bereikt" bij de gast staan
   * terwijl het allang weer werkt - en dan probeert hij het niet eens meer.
   */
  | { type: 'budgetVrij' }
  | { type: 'error'; code: ErrorCode; detail?: string }
  | { type: 'roomClosed'; reason: CloseReason }
  | { type: 'pong' };

// ---------------------------------------------------------------------------
// Hulpjes
// ---------------------------------------------------------------------------

export const otherSlot = (slot: Slot): Slot => (slot === 'a' ? 'b' : 'a');
